import React, { RefObject } from 'react'
import MaskedThousandsSeparatorInput from './textWithThousandsSeparator.inputs'
import TextInput from './text.inputs'
// import RadioInput, { Direction } from '../RadioInput';
import RadioInput from './radio.input'
import { RadioList, RequestInputs } from '@utils/request'
import { Direction } from '@services/constants'
import { onKeyPressNumberInput } from '../__forms.utils'
import { Colors, Constants } from '@utils/css-variables'
import { joinClasses, makeClasses } from '@utils/styles'
import Breakpoints from '@utils/breakpoints'
import * as pageUtils from './_inputs.utils'
import Error from '@components/requestForms/inputs/error.input'
import parse from 'html-react-parser'
import { formatHtmlForStrapiText } from '@utils/methods'

// Props
interface MeasurementInputProps {
	measurementInput: RequestInputs
	unitInput: RequestInputs
	listMeasurementUnit: RadioList[]
	maxCharacters: number
	hasThousandsSeparator?: boolean
	isSummaryStep: boolean
	errors: string[]
	hasError: boolean
	reducedMargin?: boolean
	disableWarningTooltip?: boolean
	onFixError: React.Dispatch<React.SetStateAction<string[]>>
	updateInput?: (name: string, value: string) => void
	onSetInput?: (value: string) => void
	onSetMeasurementInput?: (value: string) => void
}

type Classes = {
	reducedMargin: string
	input: string
	error: string
	description: string
	required: string
	hasHtmlRequired: string
	contentRoot: string
	noWrap: string
	toolTipPopup: string
	wrapTitle: string
	answerURB: string
	subContent2: string
	measurementField: string
}

const classes: Classes = makeClasses({
	reducedMargin: {
		marginBottom: '-10px'
	},
	input: {
		fontFamily: Constants.fontStack,
		fontSize: '16px',
		width: '100%',
		background: Colors.white,
		border: `1px solid ${Colors.grey}`,
		fontWeight: 400,
		padding: '8px 12px',
		borderRadius: '0px',
		lineHeight: '26px',
		'&:focus': {
			outline: 'none'
		}
	},
	error: {
		'& > label': {
			color: Colors.errorRed
		},
		span: {
			color: Colors.errorRed
		}
		// input: {
		// 	borderColor: Colors.errorRed,
		// 	'&:focus': {
		// 		borderColor: Colors.errorRed,
		// 		boxShadow: `0 0 0 2px ${Colors.errorRedTransparent20}`
		// 	}
		// }
	},
	description: {
		clear: 'both',
		display: 'block',
		margin: '5px 0px 3px',
		padding: '0',
		fontSize: '0.85em',
		lineHeight: '1.25em',
		color: Colors.lightGrey2,
		fontWeight: '400'
	},

	required: {
		'&::after': {
			content: '"*"',
			color: Colors.ligthPurple,
			fontSize: '1.2em',
			marginLeft: '5px'
		}
	},
	hasHtmlRequired: {
		'& p::after': {
			content: '"*"',
			color: Colors.ligthPurple,
			fontSize: '1.2em',
			marginLeft: '5px'
		}
	},
	contentRoot: {
		display: 'inline-block',
		paddingRight: '1em'
	},
	noWrap: {
		position: 'relative',
		whiteSpace: 'nowrap',
		'&::after': {
			content: '"*"',
			position: 'absolute',
			top: '45%',
			left: 12,
			transform: 'translateY(-50%)',
			color: Colors.ligthPurple,
			fontSize: '1.2em'
		}
	},
	toolTipPopup: {
		'& div span': {
			paddingRight: '0px !important'
		}
	},
	wrapTitle: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'start',
		'& .tooltip-content': {
			left: '0!important',
			right: 'auto !important'
		}
	},
	subContent2: {
		display: 'grid',
		columnGap: '15px',
		rowGap: '10px',
		gridTemplateColumns: '2fr auto',
		alignItems: 'end',
		[Breakpoints.maxWidth('md')]: {
			gridTemplateColumns: '2fr auto'
		},
		[Breakpoints.maxWidth('sm')]: {
			gridTemplateColumns: '2fr auto'
		},
		[Breakpoints.minWidth('md')]: {
			'& span': {
				'white-space': 'nowrap'
			}
		}
	},
	measurementField: {
		// allow shrinking on very small screens where we stack vertically
		[Breakpoints.maxWidth('lg')]: {
			'& input': {
				minWidth: '150px',
			},
		},
	}
})

const MeasurementInput: React.FC<MeasurementInputProps> = ({
	measurementInput,
	unitInput,
	listMeasurementUnit,
	maxCharacters,
	hasThousandsSeparator = false,
	isSummaryStep,
	hasError,
	errors,
	reducedMargin,
	disableWarningTooltip = false,
	onFixError,
	updateInput,
	onSetInput,
	onSetMeasurementInput
}) => {
	const handleMeasurementInputChange = (value, inputName, maxCharacters) => {
		const cleanedValue = value.split(/[.,]/g)[0]

		if (cleanedValue.length <= maxCharacters) {
			updateInput && updateInput(inputName, value)
		}
	}

	const handleOnSetInput = (value, maxCharacters) => {
		const cleanedValue = value.split(/[.,]/g)[0]

		if (cleanedValue.length <= maxCharacters) {
			onSetInput && onSetInput(value)
		}
	}

	const getLabelFromList = (list, value) => {
		return list?.find((element) => element.value == value?.toString())?.label
	}

	const getLabel = () => {
		const label = measurementInput.label
		const isRequired = measurementInput.required
		// if (hasHtmlLabel) {
		// 	return (
		// 		<span className={measurementInput.required ? classes.hasHtmlRequired : ''}>
		// 			{parse(formatHtmlForStrapiText(label))}
		// 		</span>
		// 	)
		// }

		const hasQuestionMark = label?.endsWith('?')
		if (hasQuestionMark) {
			return (
				<div className={classes.contentRoot}>
					{label?.replace('?', '')}
					<span className={isRequired ? classes.noWrap : ''}>?</span>
				</div>
			)
		} else {
			return <span className={isRequired ? classes.required : ''}>{label}</span>
		}
	}

	// If isSummary is true, we return a simple label with the selected value
	if (isSummaryStep) {
		if (
			measurementInput.value &&
			!measurementInput.value?.includes('undefined')
		) {
			return (
				<div>
					<strong>{measurementInput.label}</strong>

					<span
						className={joinClasses([pageUtils.classes.answerURB, 'answerURB'])}
					>
						{measurementInput.value}{' '}
						{getLabelFromList(listMeasurementUnit, unitInput.value)}
					</span>
				</div>
			)
		}
		return null
	}

	return (
		<div
			className={joinClasses([
				reducedMargin ? classes.reducedMargin : '',
				hasError ? classes.error : ''
			])}
		>
			<label htmlFor={measurementInput.name} style={{ paddingBottom: '0px' }}>
				<span>
					<div className={classes.wrapTitle}>{getLabel()}</div>
					{typeof measurementInput.description === 'string' && (
						<span className={classes.description}>
							{measurementInput.description}
						</span>
					)}
				</span>
				<Error
					hasError={hasError}
					errorText={
						measurementInput.value !== ''
							? 'Veuillez entrer un nombre valide.'
							: undefined
					}
				/>
			</label>

			<div className={classes.subContent2}>
				{hasThousandsSeparator ? (
					<div className={classes.measurementField}>
						<MaskedThousandsSeparatorInput
							id={measurementInput.name}
							label={''}
							value={measurementInput.value}
							onChange={(value) =>
								updateInput
									? handleMeasurementInputChange(
											value,
											measurementInput.name,
											maxCharacters
										)
									: handleOnSetInput(value, maxCharacters)
							}
							hasError={hasError}
							ref={measurementInput.ref as RefObject<HTMLInputElement>}
							errors={errors}
							setError={onFixError}
							maxCharacters={maxCharacters}
							disableErrorText
						/>
					</div>
				) : (
					<div className={classes.measurementField}>
						<TextInput
							id={measurementInput.name}
							type={disableWarningTooltip ? 'text' : 'number'}
							label={''}
							value={measurementInput.value ?? ''}
							onChange={(value) =>
								updateInput
									? handleMeasurementInputChange(
											value,
											measurementInput.name,
											maxCharacters
										)
									: handleOnSetInput(value, maxCharacters)
							}
							hasError={hasError}
							customErrorText={
								measurementInput.value !== ''
									? 'Veuillez entrer un nombre valide.'
									: undefined
							}
							onKeyPress={onKeyPressNumberInput}
							min="0"
							maxCharacters={maxCharacters}
							// description={measurementInput.description ?? ''}
							disableErrorText
						/>
					</div>
				)}

				<RadioInput
					name={unitInput.name}
					label={''}
					value={unitInput.value?.toString()}
					list={listMeasurementUnit}
					direction={Direction.horizontal}
					// hasError={errors!.includes(unitInput.name)}
					onChange={(value) =>
						updateInput
							? updateInput(unitInput.name, value)
							: onSetMeasurementInput && onSetMeasurementInput(value)
					}
					hasBackgroundWrapper={true}
				/>
			</div>
		</div>
	)
}

export default MeasurementInput
